import PageBreadcrumb from "../../components/common/CustomBreadCrumb";
import PageMeta from "../../components/common/PageMeta";
import ComponentCard from "../../components/common/ComponentCard";
import Label from "../../components/form/Label";
import TextArea from "../../components/form/input/TextArea";
import { useRef, useState } from "react";
import useGoBack from "../../hooks/useGoBack";
import { useCreateCategory } from "@/hooks/useCategories";
import Radio from "@/components/form/input/Radio";
import ImageCropModal from "../Flashcard/ImageCropModal";
import { getImageDimensions } from "@/utils/validation";
import { base64ToFile, resizeImageToFitMinimumSize } from "@/utils/reSizeImage";
import { toast } from "react-toastify";
import Input from "@/components/form/input/InputField";
import { useTranslation } from "react-i18next";

export default function CreateCategories() {
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [showCropModal, setShowCropModal] = useState(false);
    const [tempImage, setTempImage] = useState("");
    const [selectedUrlOrUpload, setSelectedUrlOrUpload] = useState({
        image: "Upload",
    });
    const { t } = useTranslation();
    const [image, setImage] = useState<string>("");
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [categoryData, setCategoryData]: any = useState({
        title: "",
        image: "",
    });

    const [errors, setErrors] = useState({
        title: "",
        image: "",
    });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const goBack = useGoBack();
    const { mutate: createCategory, isLoading } = useCreateCategory();

    const validate = () => {
        const newErrors = {
            title: categoryData.title.trim() === "" ? t("Title is required") : "",
            image: !categoryData.image ? t("Image is required") : "",
        };

        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error !== "");
    };

    const handleInputChange = (field: "title", value: string) => {
        setCategoryData((prev: any) => ({ ...prev, [field]: value }));
        setErrors(prev => ({ ...prev, [field]: "" }));
    };

    const handleCreateDeck = async () => {
        if (!validate()) return;
        const fd: any = new FormData();
        for (let key of Object.keys(categoryData)) {
            fd.append(key, categoryData[key]);
        }
        createCategory(
            fd,
            {
                onSuccess: () => {
                    setTimeout(() => {
                        window.location.href = "/list-categories";
                    }, 1500);
                },
            }
        );
    };

    const simulateProgress = (setProgress: (progress: number) => void, onComplete: () => void) => {
        let progress = 0;
        const interval = setInterval(() => {
            progress += 5;
            if (progress >= 100) {
                clearInterval(interval);
                onComplete();
            } else {
                setProgress(progress);
            }
        }, 50);
    };

    const handleRadioChangeImage = (value: string) => {
        setSelectedUrlOrUpload({
            ...selectedUrlOrUpload,
            image: value
        });
        setImage("");
        setCategoryData({
            ...categoryData,
            image: "",
        })
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    return (
        <div>
            <PageMeta
                title="VIYUME - VIYUME Dashboard"
                description="Create New Category"
            />
            <div className="mx-auto max-w-[80%] grid grid-cols-1 gap-6">
                <div className="space-y-6">
                    <ComponentCard title="">
                        <div className="space-y-6">
                            <div>
                                <Label>{t("Title")}</Label>
                                <TextArea
                                    value={categoryData.title}
                                    onChange={(value) => handleInputChange("title", value)}
                                    rows={3}
                                    placeholder={t("Enter category title")}
                                    disabled={isLoading}
                                />
                                {errors.title && (
                                    <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                                )}
                            </div>
                            <div>
                                <Label className='mt-6'>{t("Image")}</Label>
                                <div className="flex flex-wrap items-center gap-8">
                                    <Radio
                                        id="radio3"
                                        name="choiceImage"
                                        value="URL"
                                        checked={selectedUrlOrUpload.image === "URL"}
                                        onChange={handleRadioChangeImage}
                                        label="URL"
                                    />
                                    <Radio
                                        id="radio4"
                                        name="choiceImage"
                                        value="Upload"
                                        checked={selectedUrlOrUpload.image === "Upload"}
                                        onChange={handleRadioChangeImage}
                                        label="Upload"
                                    />
                                </div>
                                {selectedUrlOrUpload.image === "Upload" ?
                                    <>
                                        {showCropModal && (
                                            <ImageCropModal
                                                image={tempImage}
                                                onCropComplete={(croppedImage) => {
                                                    setImage(croppedImage);
                                                    setCategoryData({
                                                        ...categoryData,
                                                        image: base64ToFile(croppedImage, `${new Date().getTime()}.jpg`),
                                                    });
                                                    setShowCropModal(false);
                                                }}
                                                onClose={() => {
                                                    setTempImage("");
                                                    setShowCropModal(false);
                                                }}
                                                onCancel={() => {
                                                    setTempImage("");
                                                    setImage("");
                                                    setCategoryData({
                                                        ...categoryData,
                                                        image: "",
                                                    });
                                                    if (fileInputRef.current) {
                                                        fileInputRef.current.value = "";
                                                    }
                                                    setShowCropModal(false);
                                                }}
                                            />
                                        )}
                                        <div className="flex flex-col space-y-2 mt-5 max-w-xs">
                                            <div className="relative">
                                                <input
                                                    ref={fileInputRef}
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={async (e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) {
                                                            const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
                                                            if (!validImageTypes.includes(file.type)) {
                                                                setErrors((prev: any) => ({
                                                                    ...prev,
                                                                    image: 'Please select a valid image file (JPEG, PNG, GIF, WEBP)'
                                                                }));
                                                                setImage("");
                                                                setCategoryData({
                                                                    ...categoryData,
                                                                    image: "",
                                                                })
                                                                if (fileInputRef.current) {
                                                                    fileInputRef.current.value = "";
                                                                }
                                                                return;
                                                            }

                                                            try {
                                                                const dimensions: any = await getImageDimensions(file);
                                                                if (dimensions) {
                                                                    if (dimensions.width < 800 || dimensions.width > 1200 || dimensions.height < 800 || dimensions.height > 1200) {
                                                                        const resizedFile: any = await resizeImageToFitMinimumSize(file, 400, 400);
                                                                        setErrors((prev: any) => ({
                                                                            ...prev,
                                                                            image: ""
                                                                        }));
                                                                        setUploadProgress(0);
                                                                        const reader = new FileReader();

                                                                        simulateProgress(setUploadProgress, () => {
                                                                            reader.readAsDataURL(resizedFile);
                                                                        });
                                                                        reader.onload = (event) => {
                                                                            if (event.target?.result) {
                                                                                const base64String = event.target.result.toString();
                                                                                setUploadProgress(100);
                                                                                setTempImage(base64String);
                                                                                setTimeout(() => {
                                                                                    setUploadProgress(0);
                                                                                    setShowCropModal(true);
                                                                                }, 1000);
                                                                            }
                                                                        };
                                                                        reader.onerror = () => {
                                                                            toast.error('Error reading file');
                                                                            setUploadProgress(0);
                                                                        };
                                                                        return
                                                                    }
                                                                }
                                                            } catch (error) {
                                                                setErrors((prev: any) => ({
                                                                    ...prev,
                                                                    image: 'Failed to read image dimensions'
                                                                }));
                                                                setImage("");
                                                                setCategoryData({
                                                                    ...categoryData,
                                                                    image: "",
                                                                })
                                                                if (fileInputRef.current) {
                                                                    fileInputRef.current.value = "";
                                                                }
                                                                return;
                                                            }

                                                            setErrors((prev: any) => ({
                                                                ...prev,
                                                                image: ""
                                                            }));
                                                            setUploadProgress(0);
                                                            const reader = new FileReader();

                                                            simulateProgress(setUploadProgress, () => {
                                                                reader.readAsDataURL(file);
                                                            });
                                                            reader.onload = (event) => {
                                                                if (event.target?.result) {
                                                                    const base64String = event.target.result.toString();
                                                                    setUploadProgress(100);
                                                                    setTempImage(base64String);
                                                                    setTimeout(() => {
                                                                        setUploadProgress(0);
                                                                        setShowCropModal(true);
                                                                    }, 1000);
                                                                }
                                                            };
                                                            reader.onerror = () => {
                                                                toast.error('Error reading file');
                                                                setUploadProgress(0);
                                                            };
                                                        }
                                                    }}
                                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                                    id="image-upload"
                                                />
                                                <label
                                                    htmlFor="image-upload"
                                                    className="flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-lg transition-colors cursor-pointer font-medium"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                                                    </svg>
                                                    {t("Upload Image")}
                                                </label>
                                            </div>
                                        </div>



                                        {uploadProgress > 0 && uploadProgress < 100 && (
                                            <div className="mt-2">
                                                <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                                                    <div
                                                        className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300 ease-in-out"
                                                        style={{ width: `${uploadProgress}%` }}
                                                    ></div>
                                                </div>
                                                <p className="text-sm text-gray-500 mt-1">{uploadProgress}% uploaded</p>
                                            </div>
                                        )}
                                        {image && (
                                            <div className="mt-4 relative inline-flex items-center gap-2">
                                                <img
                                                    src={image}
                                                    alt="Preview"
                                                    className="max-w-xs rounded-lg shadow-lg"
                                                    onClick={() => setIsModalOpen(true)}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setImage("");
                                                        setCategoryData({
                                                            ...categoryData,
                                                            image: "",
                                                        })
                                                        if (fileInputRef.current) {
                                                            fileInputRef.current.value = "";
                                                        }
                                                    }}
                                                    className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 focus:outline-none"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </div>
                                        )}
                                    </> :
                                    <>
                                        <Input
                                            type="text"
                                            value={categoryData.image}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                setCategoryData({ ...categoryData, image: e.target.value })
                                                setImage(e.target.value)
                                            }}
                                            placeholder="Please enter image URL"
                                            className="mt-2"
                                        />
                                        {image && (
                                            <div className="mt-4 relative inline-flex items-center gap-2">
                                                <img
                                                    src={image}
                                                    alt="Preview"
                                                    className="max-w-xs rounded-lg shadow-lg"
                                                    onClick={() => setIsModalOpen(true)}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setImage("");
                                                        setCategoryData({
                                                            ...categoryData,
                                                            image: "",
                                                        })
                                                        if (fileInputRef.current) {
                                                            fileInputRef.current.value = "";
                                                        }
                                                    }}
                                                    className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 focus:outline-none"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </div>
                                        )}
                                    </>
                                }
                                {errors.image && (
                                    <p className="mt-1 text-sm text-red-500">{errors.image}</p>
                                )}
                            </div>
                            <div className="flex justify-end space-x-4">
                                <button
                                    onClick={goBack}
                                    className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
                                    disabled={isLoading}
                                >
                                    {t("Cancel")}
                                </button>
                                <button
                                    type="button"
                                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                    onClick={() => {
                                        const hasErrors = validate();
                                        if (hasErrors) {
                                            handleCreateDeck()
                                        }
                                    }}
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            {t("Creating")}  ...

                                        </div>
                                    ) : (
                                        <> {t("Create")} </>
                                    )}
                                </button>
                            </div>
                        </div>
                        {isModalOpen && (
                            <>
                                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-70">
                                    <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl p-4 relative m-4">
                                        <button
                                            onClick={() => setIsModalOpen(false)}
                                            className="absolute -top-2 -right-2 z-50 bg-gray-800 text-white hover:bg-gray-900 w-8 h-8 rounded-full flex items-center justify-center text-lg transition-colors"
                                        >
                                            &times;
                                        </button>
                                        <div className="h-[80vh] flex items-center justify-center">
                                            <img
                                                src={image}
                                                alt="Preview"
                                                className="max-h-full max-w-full object-contain rounded-md"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </ComponentCard>
                </div>
            </div>
        </div>
    );
}
